/**
 * Issue filters: one state shape, held in the URL.
 *
 * Keeping filters in the query string means a filtered board is a link someone
 * can paste into a comment, and the back button undoes a filter change. The
 * same shape drives both the server-side `/issues` query and the client-side
 * narrowing the board and backlog apply to an already-loaded response.
 *
 * The board endpoint returns whole columns with their ranks intact, so filters
 * there are applied in the browser rather than round-tripped: a drag still has
 * real neighbours to compute a rank between, which is exactly what the move
 * endpoint expects.
 */

import { useCallback, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'

import type { IssueQuery } from './api'
import type { Issue, IssueType, TaskPriority, TaskStatus } from './types'

export interface IssueFilters {
  /** Free text over title, description and issue key. */
  q: string
  type: IssueType[]
  status: TaskStatus[]
  priority: TaskPriority[]
  /** Label ids. */
  label: string[]
  /** '', 'me', 'unassigned', or a numeric user id. */
  assignee: string
}

export const EMPTY_FILTERS: IssueFilters = {
  q: '',
  type: [],
  status: [],
  priority: [],
  label: [],
  assignee: '',
}

export const ISSUE_TYPES: IssueType[] = ['epic', 'story', 'task', 'bug', 'subtask']
export const TASK_STATUSES: TaskStatus[] = ['pending', 'in_progress', 'done']
export const TASK_PRIORITIES: TaskPriority[] = ['low', 'medium', 'high']

export const STATUS_LABELS: Record<TaskStatus, string> = {
  pending: 'To Do',
  in_progress: 'In Progress',
  done: 'Done',
}

export const PRIORITY_LABELS: Record<TaskPriority, string> = {
  low: 'Low',
  medium: 'Medium',
  high: 'High',
}

export const TYPE_LABELS: Record<IssueType, string> = {
  epic: 'Epic',
  story: 'Story',
  task: 'Task',
  bug: 'Bug',
  subtask: 'Subtask',
}

function splitParam(raw: string | null): string[] {
  if (!raw) return []
  return raw
    .split(',')
    .map((value) => value.trim())
    .filter(Boolean)
}

export function countActiveFilters(filters: IssueFilters): number {
  return (
    (filters.q.trim() ? 1 : 0) +
    (filters.type.length > 0 ? 1 : 0) +
    (filters.status.length > 0 ? 1 : 0) +
    (filters.priority.length > 0 ? 1 : 0) +
    (filters.label.length > 0 ? 1 : 0) +
    (filters.assignee ? 1 : 0)
  )
}

export interface FilterController {
  filters: IssueFilters
  activeCount: number
  set: (patch: Partial<IssueFilters>) => void
  toggle: (key: 'type' | 'status' | 'priority' | 'label', value: string) => void
  clear: () => void
}

/** Read/write the filter state in the URL, without clobbering other params. */
export function useIssueFilters(): FilterController {
  const [params, setParams] = useSearchParams()

  const filters = useMemo<IssueFilters>(
    () => ({
      q: params.get('q') ?? '',
      type: splitParam(params.get('type')) as IssueType[],
      status: splitParam(params.get('status')) as TaskStatus[],
      priority: splitParam(params.get('priority')) as TaskPriority[],
      label: splitParam(params.get('label')),
      assignee: params.get('assignee') ?? '',
    }),
    [params],
  )

  const write = useCallback(
    (next: IssueFilters) => {
      setParams(
        (current) => {
          const updated = new URLSearchParams(current)
          const apply = (key: string, value: string) => {
            if (value) updated.set(key, value)
            else updated.delete(key)
          }
          apply('q', next.q.trim())
          apply('type', next.type.join(','))
          apply('status', next.status.join(','))
          apply('priority', next.priority.join(','))
          apply('label', next.label.join(','))
          apply('assignee', next.assignee)
          return updated
        },
        { replace: true },
      )
    },
    [setParams],
  )

  const set = useCallback(
    (patch: Partial<IssueFilters>) => write({ ...filters, ...patch }),
    [filters, write],
  )

  const toggle = useCallback(
    (key: 'type' | 'status' | 'priority' | 'label', value: string) => {
      const current = filters[key] as string[]
      const next = current.includes(value)
        ? current.filter((entry) => entry !== value)
        : [...current, value]
      write({ ...filters, [key]: next } as IssueFilters)
    },
    [filters, write],
  )

  const clear = useCallback(() => write(EMPTY_FILTERS), [write])

  return { filters, activeCount: countActiveFilters(filters), set, toggle, clear }
}

/** Translate the filter state into query parameters for `GET /issues`. */
export function filtersToQuery(filters: IssueFilters): IssueQuery {
  return {
    q: filters.q.trim() || undefined,
    type: filters.type.length > 0 ? filters.type : undefined,
    status: filters.status.length > 0 ? filters.status : undefined,
    priority: filters.priority.length > 0 ? filters.priority : undefined,
    label: filters.label.length > 0 ? filters.label : undefined,
    assignee: filters.assignee || undefined,
  }
}

/**
 * The client-side twin of the server's filter, for responses that arrive whole
 * (the board and the backlog). It must agree with `applyIssueFilters` in the Go
 * handler, or the same filter would mean two different things depending on
 * which screen applied it.
 */
export function matchesFilters(
  issue: Issue,
  filters: IssueFilters,
  currentUserId: number | null,
): boolean {
  if (filters.type.length > 0 && !filters.type.includes((issue.issue_type ?? 'task') as IssueType)) {
    return false
  }
  if (filters.status.length > 0 && !filters.status.includes(issue.status)) return false
  if (filters.priority.length > 0 && !filters.priority.includes(issue.priority)) return false

  if (filters.assignee) {
    if (filters.assignee === 'me') {
      if (currentUserId === null || issue.assignee_id !== currentUserId) return false
    } else if (filters.assignee === 'unassigned') {
      if (issue.assignee_id) return false
    } else {
      const wanted = Number(filters.assignee)
      if (!Number.isFinite(wanted) || issue.assignee_id !== wanted) return false
    }
  }

  if (filters.label.length > 0) {
    const applied = new Set<string>()
    for (const label of issue.labels ?? []) {
      applied.add(label.id)
      applied.add(label.name.toLowerCase())
    }
    if (!filters.label.some((wanted) => applied.has(wanted) || applied.has(wanted.toLowerCase()))) {
      return false
    }
  }

  const term = filters.q.trim().toLowerCase()
  if (term) {
    const haystack = [issue.title, issue.description, issue.issue_key ?? '']
      .join(' ')
      .toLowerCase()
    if (!haystack.includes(term)) return false
  }

  return true
}
